import { Feather } from "@expo/vector-icons";
import { Text, ActivityIndicator, TouchableRipple } from "react-native-paper";
import { View, TouchableOpacity } from "react-native";
import { gql, useQuery } from "@apollo/client";

import { ScreenProps, EligibilityStatus } from "./types";
import {
  ActionsContainer,
  ScreenContainer,
  Button,
  ActivityImage,
  ScrollableContainer,
  LinkButton,
  Modal,
} from "../components";
import IneligibleModal from "./DeferredCompScreen/IneligibleModal";
import { useState } from "react";
import {
  ActivityUpdate,
  formatPlanDate,
  getActivityUpdates,
  isActivityUpdatedOrUnavailable,
  isEligible,
  planStartYear,
} from "./utils";
import useIntercom from "../hooks/useIntercom";
import config from "../config";
import { isDigital } from "../utils/activity";
import { colors } from "../../theme";
import { ACTIVITY_FRAGMENT, Activity, CURRENT_MEMBER_FRAGMENT, CurrentMember, Plan } from "./fragments";
import { Client as IntercomClient } from "../hooks/useIntercom";

export const ACTIVITY_QUERY = gql`
  query getActivity($code: String!) {
    currentMember {
      ...CurrentMemberFragment
    }
    currentPlan: currentDeferredCompPlan {
      id
      startsOn
      activity: activityByCode(code: $code) {
        ...ActivityFragment
      }
    }
    planTransition: deferredCompPlanTransition {
      previousPlan {
        id
        startsOn
        submissionWindowClosesOn
        activity: activityByCode(code: $code) {
          ...ActivityFragment
        }
      }
    }
  }
  ${ACTIVITY_FRAGMENT}
  ${CURRENT_MEMBER_FRAGMENT}
`;

export interface ActivityPlan extends Pick<Plan, "id" | "startsOn"> {
  activity?: Activity | null;
  submissionWindowClosesOn?: string | null;
}

interface ActivityQuery {
  currentPlan: ActivityPlan;
  planTransition?: {
    previousPlan: ActivityPlan;
  };
  currentMember: CurrentMember;
}

function PlanButton({
  selected,
  plan,
  select,
  isUpdated,
}: {
  selected: boolean;
  plan: ActivityPlan;
  select(): void;
  isUpdated: boolean;
}) {
  return (
    <TouchableRipple
      testID="plan-year-control"
      onPress={select}
      className={`flex-1 items-center justify-center rounded-lg p-4 relative border ${
        selected ? "bg-white border-primary-light" : "border-sky-lighter"
      }`}
    >
      <>
        {isUpdated && (
          <View className="bg-primary-light rounded-lg py-0.5 px-2 absolute left-0 top-0 -mt-2.5 ml-2">
            <Text className="text-sky-white text-tiny-tight">Updated</Text>
          </View>
        )}
        <Text className={`text-regular ${selected ? "font-500 text-primary-light" : "text-ink-light"}`}>
          {planStartYear(plan)} Plan
        </Text>
      </>
    </TouchableRipple>
  );
}

const openTransitionArticle = (intercom: IntercomClient) => () => {
  intercom.present(config.intercomContent.deferredCompPlanTransition);
};

function SelectPlanModal({
  currentPlan,
  previousPlan,
  visible,
  isUnavailable,
  onDismiss,
  openActivity,
}: {
  visible: boolean;
  onDismiss: () => void;
  currentPlan: ActivityPlan;
  previousPlan: ActivityPlan;
  isUnavailable: boolean;
  openActivity: (activity: Activity) => void;
}) {
  const intercom = useIntercom();
  const formattedCurrentPlanDate = formatPlanDate(currentPlan.startsOn);

  return (
    <Modal title="" visible={visible} hasBorder={false} onDismiss={onDismiss}>
      {!isUnavailable && (
        <>
          <Text className="text-title-5 font-700 text-ink-darkest text-center">Select Plan Year</Text>
          <View className="items-center p-6">
            <Text className="font-400 text-regular-normal">
              This activity has been updated for the {planStartYear(currentPlan)} plan year. Activities completed prior to{" "}
              {formattedCurrentPlanDate}, must be submitted to the {planStartYear(previousPlan)} Plan.
            </Text>

            <Text className="font-400 text-regular-normal my-2">Questions about updated activities?</Text>
            <LinkButton onPress={openTransitionArticle(intercom)}>Learn More</LinkButton>
          </View>
        </>
      )}

      <ActionsContainer insideModal hideBorder={isUnavailable}>
        <View className="w-full">
          {isUnavailable ? (
            <>
              <Text className="font-400 text-regular-normal text-center mb-6 -mt-4">
                Activity must have concluded before {formattedCurrentPlanDate} to be eligible for submission.
              </Text>
              <Button
                className="mb-2"
                mode="contained"
                onPress={() => previousPlan.activity && openActivity(previousPlan.activity)}
              >
                Submit Points for {planStartYear(previousPlan)} Plan
              </Button>
            </>
          ) : (
            <>
              <Text className="font-400 text-small-tight text-center mb-2">
                Activity concluded on/after {formattedCurrentPlanDate}
              </Text>
              <Button mode="contained" onPress={() => currentPlan.activity && openActivity(currentPlan.activity)}>
                Submit Points for {planStartYear(currentPlan)} Plan
              </Button>

              <Text className="font-400 text-small-tight text-center mt-6 mb-2">
                Activity concluded before {formattedCurrentPlanDate}
              </Text>
              <Button
                className="mb-2"
                mode="contained"
                onPress={() => previousPlan.activity && openActivity(previousPlan.activity)}
              >
                Submit Points for {planStartYear(previousPlan)} Plan
              </Button>
            </>
          )}
        </View>
      </ActionsContainer>
    </Modal>
  );
}

function ActivityScreen({ navigation, route }: ScreenProps<"Activity">) {
  const intercom = useIntercom();
  const { data } = useQuery<ActivityQuery>(ACTIVITY_QUERY, {
    variables: route.params,
  });
  const eligibilityStatus = data?.currentMember?.eligibilityStatus || EligibilityStatus.INACTIVE;
  const enrolled = data?.currentMember.enrolled || false;
  const [showIneligibleModal, setShowIneligibleModal] = useState(false);
  const [showCurrentActivity, setShowCurrentActivity] = useState(true);
  const [showSelectPlanModal, setShowSelectPlanModal] = useState(false);
  const currentPlan = data?.currentPlan;
  const previousPlan = data?.planTransition?.previousPlan?.submissionWindowClosesOn
    ? data.planTransition.previousPlan
    : undefined;
  const activity = showCurrentActivity ? currentPlan?.activity : previousPlan?.activity;
  const activityUpdates =
    data?.planTransition && previousPlan ? getActivityUpdates(currentPlan?.activity, previousPlan?.activity) : [];
  const activityImageUrl = activity?.imageUrl || previousPlan?.activity?.imageUrl;
  const activityName = activity?.name || previousPlan?.activity?.name;
  const activityUpdatedOrUnavailable = isActivityUpdatedOrUnavailable(activityUpdates);

  if (!currentPlan)
    return (
      <View className="h-full justify-center">
        <ActivityIndicator />
      </View>
    );

  const formattedCurrentPlanDate = formatPlanDate(currentPlan.startsOn);

  const actions = ((eligible: boolean) => {
    const navigateIfEligible = (route: "ActivityRequestPoints" | "Enroll", params?: any) =>
      eligible
        ? () => {
            if (route === "ActivityRequestPoints" && activityUpdatedOrUnavailable) {
              setShowSelectPlanModal(true);
            } else {
              navigation.navigate(route, params);
            }
          }
        : () => {
            setShowIneligibleModal(true);
          };

    if (activity && isDigital(activity)) {
      return (
        <View className="sm:flex-column">
          <View className="items-center sm:items-start">
            <Text className="font-400 text-regular-tight text-ink-darker">
              Points for this activity are automatically awarded once completed.
            </Text>

            <Button
              className="mt-4"
              mode="contained"
              onPress={() => {
                intercom.present(config.intercomContent.deferredCompAutomaticActivityLearnMore);
              }}
            >
              Learn more
            </Button>
          </View>
        </View>
      );
    }

    if (enrolled) {
      return (
        <Button
          onPress={navigateIfEligible("ActivityRequestPoints", {
            code: activity?.code,
          })}
          mode="contained"
        >
          Submit Points for Activity
        </Button>
      );
    }

    return (
      <View className="sm:flex-column">
        <View className="items-center">
          <Text className="font-400 text-regular-tight text-ink-darker">Enroll to start earning points today.</Text>
        </View>

        <Button className="mt-4" mode="contained" onPress={navigateIfEligible("Enroll")}>
          Enroll
        </Button>
      </View>
    );
  })(isEligible(eligibilityStatus));

  const updatedBadge = (section: ActivityUpdate) =>
    currentPlan && showCurrentActivity && activityUpdates.includes(section) ? (
      <View className="mb-2 flex-row" testID={`update-badge-${ActivityUpdate[section]}`}>
        <View className="bg-sky-light rounded-lg py-0.5 px-2 shrink">
          <Text className="text-primary font-500 text-tiny-tight">Updated for {planStartYear(currentPlan)}</Text>
        </View>
      </View>
    ) : null;

  return (
    <ScreenContainer testID="activity-screen" navigationTitle={activityName} goBack={() => navigation.navigate("Activities")}>
      <ScrollableContainer>
        {activityUpdates.length > 0 && (
          <TouchableOpacity
            onPress={openTransitionArticle(intercom)}
            activeOpacity={0.7}
            className="bg-yellow-lightest flex-row items-center px-4 py-3 justify-between"
            testID="update-notice"
          >
            <View className="flex-row items-center shrink">
              <Feather name="info" color={colors.yellow.dark} size={24} />
              <Text className="mx-4">
                {activityUpdates.includes(ActivityUpdate.New)
                  ? `Activity newly added for the ${planStartYear(currentPlan)} plan year.`
                  : activityUpdates.includes(ActivityUpdate.Unavailable)
                  ? `Activity has been removed for the ${planStartYear(currentPlan)} plan year.`
                  : `Activity updated for the ${planStartYear(currentPlan)} plan year.`}
              </Text>
            </View>
            <Text className="text-primary shrink-0 pl-12">Learn More</Text>
          </TouchableOpacity>
        )}

        <View className="items-center sm:mt-8">
          <ActivityImage imageUrl={activityImageUrl} imageClassName="w-full h-60 sm:h-96 sm:rounded-lg bg-sky-light" />
        </View>

        <View className="p-6">
          {activityUpdatedOrUnavailable && previousPlan && (
            <View className="flex-row bg-sky-lighter rounded-lg mb-8">
              <PlanButton
                plan={currentPlan}
                isUpdated={!activityUpdates.includes(ActivityUpdate.Unavailable)}
                select={() => {
                  setShowCurrentActivity(true);
                }}
                selected={showCurrentActivity}
              />
              <PlanButton
                plan={previousPlan}
                isUpdated={false}
                select={() => {
                  setShowCurrentActivity(false);
                }}
                selected={!showCurrentActivity}
              />
            </View>
          )}

          {activityUpdates.includes(ActivityUpdate.Unavailable) && previousPlan && showCurrentActivity ? (
            <View className="bg-sky-lightest opacity-70 p-4 rounded-sm border border-sky-light">
              <Text className="font-700 text-title-3 mb-4">{previousPlan?.activity?.name}</Text>
              <Text className="font-400 text-regular-normal">
                This activity has been removed from the {planStartYear(currentPlan)} Deferred Compensation Plan Year and is no
                longer available for activities that concluded on or after {formattedCurrentPlanDate}.{"\n\n"}
                Qualifying activity submissions that concluded before {formattedCurrentPlanDate} can be submitted to the{" "}
                <Text
                  className="font-400 text-primary text-regular-normal underline"
                  onPress={() => setShowCurrentActivity(false)}
                >
                  {planStartYear(previousPlan)} Deferred Compensation Plan Year
                </Text>{" "}
                {previousPlan.submissionWindowClosesOn ? `until ${formatPlanDate(previousPlan.submissionWindowClosesOn)}` : ""}.
              </Text>
              <View className="items-center mt-4">
                <Text className="font-400 text-regular-normal mb-2">Questions about removed activities?</Text>
                <LinkButton onPress={openTransitionArticle(intercom)}>Learn More</LinkButton>
              </View>
              <View className="bg-sky-light h-[1px] -mx-4 my-4" />
              <View className="items-center">
                <Text className="font-400 text-regular-normal text-center mb-2">
                  Activities that conclude prior to {formattedCurrentPlanDate}, can be submitted to the{" "}
                  {planStartYear(previousPlan)} plan.
                </Text>
                <LinkButton onPress={() => setShowCurrentActivity(false)}>
                  Submit Points for {planStartYear(previousPlan)} Plan Year
                </LinkButton>
              </View>
            </View>
          ) : (
            <>
              {updatedBadge(ActivityUpdate.NameUpdated)}
              <Text className="font-700 text-title-3 mb-4">{activity?.name}</Text>
              {updatedBadge(ActivityUpdate.DescriptionUpdated)}
              <Text className="font-400 text-regular-normal">{activity?.description}</Text>
              <View className="my-8 border-b border-b-sky" />
              {updatedBadge(ActivityUpdate.ParticipationLevelUpdated)}
              <Text className="mb-6 font-700 text-title-4">Activity Levels</Text>
              {activity?.participationLevels.map(({ points, requirements, id }) => (
                <View className="mb-8" key={id}>
                  <Text className="font-700 text-title-5">{points} Points</Text>
                  <Text className="mt-1 font-400 text-regular-normal">{requirements}</Text>
                </View>
              ))}
            </>
          )}
        </View>
      </ScrollableContainer>
      <IneligibleModal
        visible={showIneligibleModal}
        onDismiss={() => {
          setShowIneligibleModal(false);
        }}
        eligibilityStatus={data?.currentMember?.eligibilityStatus}
        enrolled={enrolled}
      />

      {showCurrentActivity && activityUpdates.includes(ActivityUpdate.Unavailable) ? undefined : (
        <ActionsContainer>{actions}</ActionsContainer>
      )}
      {activityUpdatedOrUnavailable && previousPlan && (
        <SelectPlanModal
          visible={showSelectPlanModal}
          isUnavailable={activityUpdates.includes(ActivityUpdate.Unavailable)}
          onDismiss={() => setShowSelectPlanModal(false)}
          currentPlan={currentPlan}
          previousPlan={previousPlan}
          openActivity={(activity) => {
            setShowSelectPlanModal(false);
            navigation.navigate("ActivityRequestPoints", { code: activity.code, id: activity.id });
          }}
        />
      )}
    </ScreenContainer>
  );
}

export default ActivityScreen;
